// ============================================================================
// PsyResultsHistory — РЕЗУЛЬТАТЫ и ИСТОРИЯ ПРОХОЖДЕНИЯ методик.
//
// Без таблиц и окон: список карточек (телефон/десктоп одинаково), новые сверху,
// «Показать ещё» вместо длинной простыни. Показывает:
//   • протоколы (результаты): методика, дата, балл, порог, заключение;
//   • ожидающие назначения (если переданы) — методика назначена, результата нет;
//   • явные состояния: нет прав на протоколы, пусто, пусто по фильтру.
//
// SSoT — сервер (протоколы типов опросов и psy_assignments); компонент только
// отображает переданные данные.
// ============================================================================

import { useMemo, useState } from 'react';
import { ClipboardList, FlaskConical, Hourglass, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatValue } from '@/lib/format';
import type { PatientResult, PsyAssignment } from '@/lib/psyWorkspace';

export interface PsyResultsHistoryProps {
  results: PatientResult[];
  /** Назначения пациента — показываются как «ожидают прохождения». */
  assignments?: PsyAssignment[];
  /** Подпись методики по коду (если банк методик уже загружен). */
  methodName?: (code: string) => string;
  emptyText?: string;
  /** Сервер не выдал чтение части протоколов — честное ограничение. */
  denied?: boolean;
  pageSize?: number;
  /** Ограничить историю одной методикой (пусто — все). */
  methodCode?: string;
  title?: string;
}

function passedTone(passed: boolean): string {
  return passed
    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
    : 'border-border bg-muted/50 text-muted-foreground';
}

export function PsyResultsHistory({
  results, assignments, methodName, emptyText, denied, pageSize = 20, methodCode, title,
}: PsyResultsHistoryProps) {
  const [visibleCount, setVisibleCount] = useState(pageSize);

  const filtered = useMemo(() => {
    const list = methodCode ? results.filter((r) => r.methodCode === methodCode) : results;
    return [...list].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
  }, [results, methodCode]);

  const pending = useMemo(() => {
    if (!assignments?.length) return [];
    const active = assignments.filter((a) => String(a.status || '').toLowerCase() === 'assigned');
    const scoped = methodCode ? active.filter((a) => a.methodCode === methodCode) : active;
    return scoped
      .filter((a) => !results.some((r) => r.methodCode === a.methodCode))
      .sort((a, b) => String(b.assignedDate).localeCompare(String(a.assignedDate)));
  }, [assignments, results, methodCode]);

  const visible = filtered.slice(0, visibleCount);
  const nameOf = (code: string, fallback?: string) => (methodName ? methodName(code) : fallback || code);

  if (denied) {
    return (
      <div className="flex items-start gap-2 rounded-lg border px-3 py-2 text-xs">
        <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
        <div>
          <div className="font-medium">Просмотр результатов ограничен</div>
          <div className="text-muted-foreground">
            Роли не выдано право «Чтение» на часть типов опросов. Обратитесь к администратору базы.
            Назначения при этом доступны.
          </div>
        </div>
      </div>
    );
  }

  if (filtered.length === 0 && pending.length === 0) {
    return (
      <div className="rounded-lg border bg-card px-3 py-3 text-xs">
        <div className="mb-1 flex items-center gap-1.5 text-sm font-medium">
          <FlaskConical className="h-3.5 w-3.5 text-primary" />
          {title || 'Результаты и история'}
        </div>
        <div className="text-muted-foreground">
          {emptyText || 'Результатов пока нет — пройдите методику, и протокол появится здесь.'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-sm font-medium">
          <FlaskConical className="h-3.5 w-3.5 text-primary" />
          {title || 'Результаты и история'}
        </div>
        <span className="text-[11px] text-muted-foreground">
          протоколов: {filtered.length}
          {pending.length > 0 ? ` · ожидают: ${pending.length}` : ''}
        </span>
      </div>

      {pending.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Ожидают прохождения</div>
          {pending.map((a) => (
            <div key={a.id} className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-xs">
              <Hourglass className="h-3.5 w-3.5 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{nameOf(a.methodCode)}</div>
                <div className="text-[11px] text-muted-foreground">
                  назначено: {a.assignedDate ? formatValue(a.assignedDate, '', 'date') : '—'}
                </div>
              </div>
              <span className="shrink-0 rounded-full border border-primary/25 bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">
                ожидает
              </span>
            </div>
          ))}
        </div>
      )}

      {visible.length > 0 && (
        <div className="space-y-1.5">
          {pending.length > 0 && (
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Протоколы</div>
          )}
          {visible.map((r) => (
            <article key={r.id} className="rounded-lg border bg-card px-3 py-2">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{nameOf(r.methodCode, r.methodName)}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {r.createdAt ? formatValue(r.createdAt, '', 'timestamp') : 'дата не указана'}
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap justify-end gap-1">
                  {r.score && (
                    <span className="rounded-full border bg-muted/40 px-1.5 py-0.5 text-[10px]">балл: {r.score}</span>
                  )}
                  <span className={`rounded-full border px-1.5 py-0.5 text-[10px] ${passedTone(r.passed)}`}>
                    {r.passed ? 'порог пройден' : 'порог не пройден'}
                  </span>
                </div>
              </div>
              {r.conclusion && (
                <details className="mt-1.5 text-xs">
                  <summary className="cursor-pointer select-none text-muted-foreground">
                    <span className="line-clamp-1 inline align-middle">{r.conclusion}</span>
                  </summary>
                  <div className="mt-1 whitespace-pre-wrap break-words text-foreground">{r.conclusion}</div>
                </details>
              )}
            </article>
          ))}
        </div>
      )}

      {filtered.length > visibleCount && (
        <Button
          size="sm"
          variant="outline"
          className="h-7 w-full text-[11px]"
          onClick={() => setVisibleCount((n) => n + pageSize)}
        >
          Показать ещё ({filtered.length - visibleCount})
        </Button>
      )}

      {filtered.length === 0 && pending.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-xs text-muted-foreground">
          <ClipboardList className="h-3.5 w-3.5" />
          Результатов пока нет — назначенные методики ещё не пройдены.
        </div>
      )}
    </div>
  );
}
